import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal } from 'antd';

import { api } from '../../api/client';
import { API_CARD_ELEMENT_SET_EXTRACT_URL } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { formatCount } from '../../utils/counts';
import { elementSetKeyFromName, namespacedElementNames } from '../../utils/conversions';
import { parseStoredTemplate } from '../../utils/displayTemplate';
import { getDataTypeMeta } from '../../utils/dictionary';
import { InputIconSelect } from '../Input';

/**
 * "Make an element set from these elements" (docs/plans/element-sets-and-me.md,
 * "Materialize / extract"; docs/platform-standards.md § The card page): some
 * of a collection card's OWN elements become a new collection element set the
 * card attaches, and leave the card — POST
 * /api/card-definitions/{id}/element-sets/extract { elementNames, name,
 * description?, icon?, color? }.
 *
 * The one time stored values MOVE: a set's element names are namespaced
 * `<key>_<slug>`, so every item's value under the old name is renamed to the
 * new one (the card's items and the descendants' that inherit the element),
 * and nothing is lost — the modal says so, and previews each rename from the
 * name the set will get.
 *
 * What cannot leave, and why, mirrors the API's gates so the refusal is read
 * here before the round trip: a driven or calculated element (a set may not
 * carry one), the title element, and an element a display template or a
 * driven config reads — on this card or any descendant (`element_in_use`).
 * The API's own refusals (`element_in_use`, `element_set_conflict`,
 * `extract_too_large`, `invalid_options`) land as an alert in the modal.
 */

/** Why an own element may not be extracted, or null when it may. */
export function extractDisabledReason(element, { card, family = [] } = {}) {
	if (!element?.name) return 'Unsaved element';
	if (element.options?.drivenFrom) return 'A driven or calculated element cannot live in an element set';
	if (element.options?.isTitle) return 'The title element cannot leave the card';
	const name = element.name;
	const cards = [card, ...family].filter(Boolean);
	for (const def of cards) {
		const template = def?.displayTemplate;
		if (template && parseStoredTemplate(template).some(seg => seg?.type === 'element' && seg.name === name)) {
			return def === card ? 'Read by the display value' : `Read by the display value of ${def.name || 'a descendant card'}`;
		}
		for (const el of (def?.elements || [])) {
			const df = el?.options?.drivenFrom;
			if (df && JSON.stringify(df).includes(name)) {
				return `Read by the driven element "${el.label?.singular || el.name}"${def === card ? '' : ` on ${def.name || 'a descendant card'}`}`;
			}
		}
	}
	return null;
}

/** The API's refusal in the modal's words. */
export function describeExtractRefusal(data) {
	if (!data) return 'The element set was not created.';
	switch (data.code) {
		case 'element_in_use': {
			const use = Array.isArray(data.uses) ? data.uses[0] : null;
			const why = use?.reason === 'display_template' ? 'a display value' : 'a driven or calculated element';
			return `"${data.element || 'That element'}" is read by ${why}; change that first.`;
		}
		case 'element_set_conflict':
			return `The name "${data.element || ''}" is already taken${data.conflictsWith?.name ? ` by ${data.conflictsWith.kind === 'set' ? 'the element set' : 'the card'} "${data.conflictsWith.name}"` : ''}${data.cardName ? ` (on ${data.cardName})` : ''}. Choose another set name.`;
		case 'extract_too_large':
			return `${(data.count || 0).toLocaleString('en-US')} stored values would move, more than one request renames (${(data.cap || 0).toLocaleString('en-US')}). Extract fewer elements at a time.`;
		default:
			return data.message || 'The element set was not created.';
	}
}

/** The card's descendants (every depth) in an id-indexed list. */
function descendantsOf(cardId, cards = []) {
	const id = normalizeId(cardId);
	if (!id) return [];
	const out = [];
	const seen = new Set([id]);
	let frontier = [id];
	while (frontier.length) {
		const current = frontier;
		const next = [];
		(cards || []).forEach(card => {
			const parent = normalizeId(card?.parentCardId);
			const own = normalizeId(card?._id);
			if (current.includes(parent) && own && !seen.has(own)) {
				seen.add(own);
				out.push(card);
				next.push(own);
			}
		});
		frontier = next;
	}
	return out;
}

export default function ExtractElementSetModal({
	open,
	// The SAVED card (never the draft: the API rewrites what is stored).
	card,
	// Every card the editor knows, for the descendants' templates and rules.
	cards = [],
	// The element sets by id, so a taken key is spotted before the request.
	setsById = {},
	onClose,
	// (data) => void — the endpoint's answer: { payload, elementSet, renamed, moved }.
	onExtracted,
}) {
	const [selected, setSelected] = useState([]);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [icon, setIcon] = useState('');
	const [color, setColor] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);

	// A fresh modal every time it opens.
	useEffect(() => {
		if (!open) return;
		setSelected([]);
		setName('');
		setDescription('');
		setIcon('');
		setColor('');
		setSubmitting(false);
		setError(null);
	}, [open]);

	const family = useMemo(() => descendantsOf(card?._id, cards), [card?._id, cards]);
	const own = useMemo(() => (card?.elements || []).filter(el => el?.name), [card]);
	const rows = useMemo(
		() => own.map(element => ({ element, reason: extractDisabledReason(element, { card, family }) })),
		[own, card, family]
	);

	// The key the API will assign: the name's slug, suffixed past the keys
	// the collection's own sets already hold (resolveKey; the set page's
	// rule), so the names previewed are the ones the values move to.
	const collectionId = normalizeId(card?.collection_id);
	const siblingKeys = useMemo(() => Object.values(setsById || {})
		.filter(set => set?.scope === 'collection' && normalizeId(set?.collection_id) === collectionId)
		.map(set => set?.key)
		.filter(Boolean), [setsById, collectionId]);
	const key = elementSetKeyFromName(name, siblingKeys);
	const chosen = useMemo(() => own.filter(el => selected.includes(el.name)), [own, selected]);
	const renames = useMemo(() => {
		const names = namespacedElementNames(chosen, key);
		return chosen.map((el, i) => ({ from: el.name, to: names[i], label: el.label?.singular || el.name }));
	}, [chosen, key]);

	const toggle = (elementName, checked) => {
		setSelected(prev => (checked ? [...prev, elementName] : prev.filter(n => n !== elementName)));
	};

	const canSubmit = Boolean(name.trim()) && chosen.length > 0 && !submitting;

	const submit = async () => {
		if (!canSubmit) return;
		setSubmitting(true);
		setError(null);
		try {
			const payload = {
				elementNames: chosen.map(el => el.name),
				name: name.trim(),
				...(description.trim() ? { description: description.trim() } : {}),
				...(icon ? { icon } : {}),
				...(color ? { color } : {}),
			};
			const response = await api.post(API_CARD_ELEMENT_SET_EXTRACT_URL(normalizeId(card?._id)), payload);
			if (onExtracted) onExtracted(response?.data || {});
			if (onClose) onClose();
		} catch (err) {
			setError(describeExtractRefusal(err?.response?.data));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			title='Make an element set'
			onCancel={onClose}
			width={640}
			destroyOnClose
			footer={[
				<Button key='cancel' onClick={onClose} disabled={submitting}>Cancel</Button>,
				<Button key='submit' type='primary' onClick={submit} disabled={!canSubmit} loading={submitting}>
					{chosen.length ? `Make the set from ${formatCount(chosen.length, 'element')}` : 'Make the set'}
				</Button>,
			]}
		>
			<p className='form-panel__hint'>
				The elements you pick become a reusable element set this collection owns, attached to this card, and leave the card&apos;s own list. Every stored value is kept: it is renamed to the set&apos;s element name, on this card&apos;s items and on any descendant&apos;s that inherits the element. Nothing is lost.
			</p>

			{error ? <Alert type='error' showIcon message={error} data-testid='extract-error' /> : null}

			<div className='micro-label form-panel__kicker'>Elements</div>
			{rows.length ? (
				<div className='extract-set__elements' role='group' aria-label='Elements to extract'>
					{rows.map(({ element, reason }) => (
						<div key={element.name} className='extract-set__element'>
							<Checkbox
								checked={selected.includes(element.name)}
								disabled={Boolean(reason) || submitting}
								onChange={event => toggle(element.name, event.target.checked)}
							>
								{element.label?.singular || element.name}
								<span className='extract-set__element-meta'>{getDataTypeMeta(element.dataType)?.label || element.dataType}</span>
							</Checkbox>
							{reason ? <span className='extract-set__reason'>{reason}</span> : null}
						</div>
					))}
				</div>
			) : (
				<p className='empty-line'>This card has no elements of its own.</p>
			)}

			<div className='micro-label form-panel__kicker'>Name</div>
			<Input
				value={name}
				onChange={event => setName(event.target.value)}
				placeholder='Contact details'
				aria-label='Element set name'
				maxLength={80}
			/>
			<p className='form-panel__hint'>
				Key <span className='extract-set__key'>{key}</span>: every element is stored under it.
			</p>
			{renames.length ? (
				<ul className='extract-set__renames' aria-label='Element names after the move'>
					{renames.map(entry => (
						<li key={entry.from}>
							<span>{entry.from}</span>
							<span className='extract-set__rename-to'>{` → ${entry.to}`}</span>
						</li>
					))}
				</ul>
			) : null}

			<div className='micro-label form-panel__kicker'>Description</div>
			<Input.TextArea
				value={description}
				onChange={event => setDescription(event.target.value)}
				aria-label='Element set description'
				autoSize={{ minRows: 2, maxRows: 4 }}
				maxLength={500}
			/>

			<div className='micro-label form-panel__kicker'>Identity</div>
			<div className='extract-set__identity'>
				<InputIconSelect
					elementName='icon'
					elementLabel='Icon and color'
					value={icon}
					onChange={(_, value) => setIcon(value || '')}
					colorElementName='color'
					colorValue={color || undefined}
					onColorChange={(_, value) => setColor(value || '')}
					recordKind='elementSet'
				/>
			</div>
		</Modal>
	);
}
